import api from './api';

export const DISTRIBUTOR_ACTIVITY_PAGE_SIZE = 20;

const adminActivityScope = (distributorId) => {
  if (!/^[a-f\d]{24}$/.test(distributorId)) throw new Error('Choose a distributor.');
  return { businessContext: 'distributor', distributorId };
};

// Only list filters may cross this boundary; caller-supplied objects never replace tenant scope.
const activityFilters = (filters = {}) =>
  Object.fromEntries(
    ['search', 'dateFrom', 'dateTo', 'status', 'orderStatus']
      .filter((key) => filters[key] !== undefined && filters[key] !== '')
      .map((key) => [key, filters[key]])
  );

const distributorService = {
  cardVerification: () => api.get('/admin/distributor-card-verification'),
  prepareCardVerification: () =>
    api.post('/admin/distributor-card-verification/prepare', {}, { skipAuthReplay: true }),
  payCardVerification: (data) =>
    api.post('/admin/distributor-card-verification/pay', data, { skipAuthReplay: true }),
  refreshCardVerification: () =>
    api.post('/admin/distributor-card-verification/status', {}, { skipAuthReplay: true }),
  adminCashDisputeControl: (id, options) =>
    api.get(`/admin/distributor-cash/${encodeURIComponent(id)}/dispute-control`, options),
  saveAdminCashDisputeControl: (id, data) =>
    api.post(`/admin/distributor-cash/${encodeURIComponent(id)}/dispute-control`, data, {
      skipAuthReplay: true,
    }),
  adminCashFundingRecovery: (id, options) =>
    api.get(`/admin/distributor-cash/${encodeURIComponent(id)}/funding-recovery`, {
      signal: options?.signal,
    }),
  recoverAdminCashFunding: (id, data) =>
    api.post(`/admin/distributor-cash/${encodeURIComponent(id)}/funding-recovery`, data, {
      skipAuthReplay: true,
    }),
  cashBankPreflight: (id, options) =>
    api.get(`/admin/distributor-cash/${encodeURIComponent(id)}/preflight`, {
      signal: options?.signal,
    }),
  activateEmptyCashBank: (id, data) =>
    api.post(`/admin/distributor-cash/${encodeURIComponent(id)}/activate-zero`, data, {
      skipAuthReplay: true,
    }),
  cashDepositOptions: (options) => api.get('/distributor/cash/deposits/options', options),
  prepareCashDeposit: (data) =>
    api.post('/distributor/cash/deposits/prepare', data, { skipAuthReplay: true }),
  currentCashDeposit: (options) => api.get('/distributor/cash/deposits/current', options),
  recoverCashDeposit: (requestKey, options) =>
    api.get('/distributor/cash/deposits/recover', { ...options, params: { requestKey } }),
  cashDeposits: (page = 1, options) =>
    api.get('/distributor/cash/deposits', { ...options, params: { page, limit: 10 } }),
  cashDeposit: (id, options) =>
    api.get(`/distributor/cash/deposits/${encodeURIComponent(id)}`, options),
  cashDepositPaymentOptions: (id, options) =>
    api.get(`/distributor/cash/deposits/${encodeURIComponent(id)}/payment-options`, options),
  payCashDeposit: (id, data) =>
    api.post(`/distributor/cash/deposits/${encodeURIComponent(id)}/pay`, data, {
      skipAuthReplay: true,
    }),
  refreshCashDepositPayment: (id) =>
    api.post(
      `/distributor/cash/deposits/${encodeURIComponent(id)}/payment-status`,
      {},
      { skipAuthReplay: true }
    ),
  closeCashDeposit: (id) =>
    api.post(
      `/distributor/cash/deposits/${encodeURIComponent(id)}/close`,
      {},
      { skipAuthReplay: true }
    ),
  adminCashFundingSettings: (id, options) =>
    api.get(`/admin/distributor-cash/${encodeURIComponent(id)}/funding-settings`, options),
  saveAdminCashFundingSettings: (id, data) =>
    api.put(`/admin/distributor-cash/${encodeURIComponent(id)}/funding-settings`, data, {
      skipAuthReplay: true,
    }),
  cashBalance: (id, options) =>
    api.get(
      id
        ? `/admin/distributor-cash/${encodeURIComponent(id)}/balance`
        : '/distributor/cash/balance',
      options
    ),
  cashSettings: (id, options) =>
    api.get(
      id
        ? `/admin/distributor-cash/${encodeURIComponent(id)}/settings`
        : '/distributor/cash/settings',
      options
    ),
  cashAlerts: (id, options) =>
    api.get(
      id ? `/admin/distributor-cash/${encodeURIComponent(id)}/alerts` : '/distributor/cash/alerts',
      options
    ),
  cashAlertPreferences: (id, options) =>
    api.get(
      id
        ? `/admin/distributor-cash/${encodeURIComponent(id)}/alert-preferences`
        : '/distributor/cash/alert-preferences',
      options
    ),
  saveCashAlertPreferences: (id, data) =>
    api.put(
      id
        ? `/admin/distributor-cash/${encodeURIComponent(id)}/alert-preferences`
        : '/distributor/cash/alert-preferences',
      data,
      { skipAuthReplay: true }
    ),
  cashEntries: (id, page = 1, options) =>
    api.get(
      id
        ? `/admin/distributor-cash/${encodeURIComponent(id)}/entries`
        : '/distributor/cash/entries',
      { ...options, params: { page, limit: 20 } }
    ),
  saveCashSettings: (id, data) =>
    api.put(`/admin/distributor-cash/${encodeURIComponent(id)}/settings`, data, {
      skipAuthReplay: true,
    }),
  cashOrderQuote: (data) =>
    api.post('/distributor/orders/cash-quote', data, { skipAuthReplay: true }),
  createWorkspaceAdmin: (data) =>
    api.post('/admin/distributors/workspace-admins', data, { skipAuthReplay: true }),
  adminPurchasePricingOptions: () => api.get('/admin/distributors/purchase-pricing-options'),
  adminDistributors: () => api.get('/admin/distributors'),
  adminCustomerOverview: (params) => api.get('/admin/distributors/customers', { params }),
  adminActivityOverview: (kind, distributorId, page = 1, status, filters) =>
    api.post(
      kind === 'orders'
        ? '/orders/search'
        : kind === 'ivrs'
          ? '/ivr/search'
          : '/admin/shipments/search',
      {
        ...(distributorId ? adminActivityScope(distributorId) : { businessContext: 'distributor' }),
        page,
        limit: DISTRIBUTOR_ACTIVITY_PAGE_SIZE,
        ...activityFilters(filters),
        ...(kind === 'ivrs' ? { outcomesVersion: 2 } : {}),
        ...(status ? { [kind === 'shipments' ? 'orderStatus' : 'status']: status } : {}),
      }
    ),
  adminAccountingStatus: (id, page = 1, options) =>
    api.get(`/admin/distributors/${encodeURIComponent(id)}/accounting-status`, {
      params: { page, limit: 10 },
      signal: options?.signal,
    }),
  adminAccountingSettings: (id, options) =>
    api.get(`/admin/distributors/${encodeURIComponent(id)}/accounting-settings`, {
      signal: options?.signal,
    }),
  saveAdminAccountingSettings: (id, data) =>
    api.put(`/admin/distributors/${encodeURIComponent(id)}/accounting-settings`, data, {
      skipAuthReplay: true,
    }),
  adminProductAccess: (id) =>
    api.get(`/admin/distributors/${encodeURIComponent(id)}/product-access`),
  saveAdminProductAccess: (id, data) =>
    api.put(`/admin/distributors/${encodeURIComponent(id)}/product-access`, data, {
      skipAuthReplay: true,
    }),
  adminProductOptions: (id) =>
    api.get(`/admin/distributors/${encodeURIComponent(id)}/product-options`),
  productOptions: () => api.get('/distributor/clinics/product-options'),
  adminPurchaseSettings: (id) =>
    api.get(`/admin/distributors/${encodeURIComponent(id)}/purchase-settings`),
  saveAdminPurchaseSettings: (id, data) =>
    api.put(`/admin/distributors/${encodeURIComponent(id)}/purchase-settings`, data, {
      skipAuthReplay: true,
    }),
  adminCustomers: (id, options) =>
    api.get(`/admin/distributors/${encodeURIComponent(id)}/customers`, options),
  adminCustomerProfile: (id, clinicId) =>
    api.get(
      `/admin/distributors/${encodeURIComponent(id)}/customers/${encodeURIComponent(clinicId)}/profile`
    ),
  saveAdminCustomerProfile: (id, clinicId, data) =>
    api.put(
      `/admin/distributors/${encodeURIComponent(id)}/customers/${encodeURIComponent(clinicId)}/profile`,
      data,
      { skipAuthReplay: true }
    ),
  saveAdminCustomerUser: (id, clinicId, userId, data) =>
    api.put(
      `/admin/distributors/${encodeURIComponent(id)}/customers/${encodeURIComponent(clinicId)}/users/${encodeURIComponent(userId)}/profile`,
      data,
      { skipAuthReplay: true }
    ),
  adminCreateCustomer: (id, data) =>
    api.post(`/admin/distributors/${encodeURIComponent(id)}/customers`, data, {
      skipAuthReplay: true,
    }),
  adminCustomer: (id, clinicId) =>
    api.get(
      `/admin/distributors/${encodeURIComponent(id)}/customers/${encodeURIComponent(clinicId)}`
    ),
  adminSaveCustomer: (id, clinicId, data) =>
    api.put(
      `/admin/distributors/${encodeURIComponent(id)}/customers/${encodeURIComponent(clinicId)}/settings`,
      data,
      { skipAuthReplay: true }
    ),
  createDistributor: (data) => api.post('/admin/distributors', data),
  separateDistributorCustomers: (id, data) =>
    api.post(`/admin/distributors/${encodeURIComponent(id)}/independent-customers`, data),
  createPreviewLogin: (id, data) =>
    api.post(`/admin/distributors/${encodeURIComponent(id)}/preview-login`, data, {
      skipAuthReplay: true,
    }),
  renewPreviewLogin: (id, previewId, data) =>
    api.post(
      `/admin/distributors/${encodeURIComponent(id)}/preview-login/${encodeURIComponent(previewId)}/renew`,
      data,
      { skipAuthReplay: true }
    ),
  addDistributorRep: (id, data) =>
    api.post(`/admin/distributors/${encodeURIComponent(id)}/reps`, data),
  adminDistributorClientOptions: (search) =>
    api.get('/admin/distributors/client-options', { params: { search } }),
  adminDistributorClinicOptions: (search) =>
    api.get('/admin/distributors/clinic-options', { params: { search } }),
  linkDistributorClinic: (id, data) =>
    api.post(`/admin/distributors/${encodeURIComponent(id)}/clinics`, data),
  adminActivity: (kind, distributorId, page = 1, status, filters) =>
    api.post(
      kind === 'orders'
        ? '/orders/search'
        : kind === 'ivrs'
          ? '/ivr/search'
          : '/admin/shipments/search',
      {
        ...adminActivityScope(distributorId),
        page,
        limit: DISTRIBUTOR_ACTIVITY_PAGE_SIZE,
        ...activityFilters(filters),
        ...(kind === 'ivrs' ? { outcomesVersion: 2 } : {}),
        ...(status ? { [kind === 'shipments' ? 'orderStatus' : 'status']: status } : {}),
      }
    ),
  adminActivityDetail: (kind, distributorId, id) =>
    api.get(
      `${kind === 'orders' ? '/orders' : kind === 'ivrs' ? '/ivr' : '/admin/shipments'}/${encodeURIComponent(id)}`,
      {
        params: adminActivityScope(distributorId),
      }
    ),
  getDashboard: () => api.get('/distributor/dashboard'),
  listClinics: (options) => api.get('/distributor/clinics', options),
  getClinicSource: (options) => api.get('/distributor/clinics/source', options),
  getClinic: (id, options) => api.get(`/distributor/clinics/${encodeURIComponent(id)}`, options),
  createClinic: (data) => api.post('/distributor/clinics', data, { skipAuthReplay: true }),
  saveSettings: (id, data) =>
    api.put(`/distributor/clinics/${encodeURIComponent(id)}/settings`, data, {
      skipAuthReplay: true,
    }),
  listUsers: (id, distributorId) =>
    api.get(
      `${distributorId ? `/admin/distributors/${encodeURIComponent(distributorId)}/customers` : '/distributor/clinics'}/${encodeURIComponent(id)}/users`
    ),
  createUser: (id, data, distributorId) =>
    api.post(
      `${distributorId ? `/admin/distributors/${encodeURIComponent(distributorId)}/customers` : '/distributor/clinics'}/${encodeURIComponent(id)}/users`,
      data,
      {
        skipAuthReplay: true,
      }
    ),
  setUserStatus: (id, userId, data, distributorId) =>
    api.put(
      `${distributorId ? `/admin/distributors/${encodeURIComponent(distributorId)}/customers` : '/distributor/clinics'}/${encodeURIComponent(id)}/users/${encodeURIComponent(userId)}/status`,
      data,
      { skipAuthReplay: true }
    ),
  resetUserPassword: (id, userId, data, distributorId) =>
    api.put(
      `${distributorId ? `/admin/distributors/${encodeURIComponent(distributorId)}/customers` : '/distributor/clinics'}/${encodeURIComponent(id)}/users/${encodeURIComponent(userId)}/password-reset`,
      data,
      { skipAuthReplay: true }
    ),
  listOrders: (page = 1, status, filters, options) =>
    api.post(
      '/orders/search',
      {
        page,
        limit: DISTRIBUTOR_ACTIVITY_PAGE_SIZE,
        ...activityFilters(filters),
        ...(status ? { status } : {}),
      },
      options
    ),
  getOrder: (id) => api.get(`/orders/${encodeURIComponent(id)}`),
  listIvrs: (page = 1, filters) =>
    api.post('/ivr/search', {
      page,
      limit: DISTRIBUTOR_ACTIVITY_PAGE_SIZE,
      ...activityFilters(filters),
      outcomesVersion: 2,
    }),
  getIvr: (id) => api.get(`/ivr/${encodeURIComponent(id)}`),
  listShipments: (page = 1, filters) =>
    api.post('/distributor/shipments/search', {
      page,
      limit: DISTRIBUTOR_ACTIVITY_PAGE_SIZE,
      ...activityFilters(filters),
    }),
  getShipment: (id) => api.get(`/distributor/shipments/${encodeURIComponent(id)}`),
  getBank: () => api.get('/distributor/bank'),
  purchaseOptions: () => api.get('/distributor/bank/purchases/options'),
  currentPurchase: () => api.get('/distributor/bank/purchases/current'),
  quotePurchase: (data) => api.post('/distributor/bank/purchase-quote', data),
  preparePurchase: (data) =>
    api.post('/distributor/bank/purchases/prepare', data, { skipAuthReplay: true }),
  recoverPurchase: (requestKey) =>
    api.post('/distributor/bank/purchases/recover', { requestKey }, { skipAuthReplay: true }),
  closePurchase: (id) =>
    api.post(
      `/distributor/bank/purchases/${encodeURIComponent(id)}/close`,
      {},
      { skipAuthReplay: true }
    ),
  listPurchases: (page = 1) =>
    api.get('/distributor/bank/purchases', { params: { page, limit: 20 } }),
  getPurchase: (id) => api.get(`/distributor/bank/purchases/${encodeURIComponent(id)}`),
  wireInstructions: () => api.get('/distributor/bank/purchases/wire-instructions'),
  purchasePaymentOptions: (id) =>
    api.get(`/distributor/bank/purchases/${encodeURIComponent(id)}/payment-options`),
  payPurchase: (id, data) =>
    api.post(`/distributor/bank/purchases/${encodeURIComponent(id)}/pay`, data, {
      skipAuthReplay: true,
    }),
  refreshPurchasePayment: (id) =>
    api.post(
      `/distributor/bank/purchases/${encodeURIComponent(id)}/payment-status`,
      {},
      { skipAuthReplay: true }
    ),
  bankAlertPreferences: () => api.get('/distributor/bank/alert-preferences'),
  saveBankAlertPreferences: (data) =>
    api.put('/distributor/bank/alert-preferences', data, { skipAuthReplay: true }),
  bankAlerts: () => api.get('/distributor/bank/alerts'),
  operationalNotices: (admin = false, page = 1, options) =>
    api.get(admin ? '/admin/distributor-operational-notices' : '/distributor/operational-notices', {
      params: { page },
      signal: options?.signal,
    }),
  adminOperationalNoticeDeliveries: (page = 1, options) =>
    api.get('/admin/distributor-operational-notices/delivery-review', {
      params: { page },
      signal: options?.signal,
    }),
  reconcileOperationalNoticeDelivery: (noticeId, userId, data) =>
    api.post(
      `/admin/distributor-operational-notices/${encodeURIComponent(noticeId)}/recipients/${encodeURIComponent(userId)}/reconcile`,
      data,
      { skipAuthReplay: true }
    ),
  listBankEntries: (familyKey, page = 1) =>
    api.get('/distributor/bank/entries', {
      params: { page, limit: 10, ...(familyKey ? { familyKey } : {}) },
    }),
  previewBankUnits: (data) => api.post('/distributor/bank/preview', data),
  orderOptions: (clinicId) =>
    api.get(`/distributor/orders/options/${encodeURIComponent(clinicId)}`),
  previewOrderShipping: (data) => api.post('/distributor/orders/shipping-preview', data),
  submitBankedOrder: (data) => api.post('/distributor/orders', data, { skipAuthReplay: true }),
  heldOrderOptions: (id, admin = false) =>
    api.get(`${admin ? '/admin' : ''}/distributor/orders/${encodeURIComponent(id)}/held-options`),
  editHeldOrder: (id, data) =>
    api.put(`/distributor/orders/${encodeURIComponent(id)}/held`, data, { skipAuthReplay: true }),
  cancelHeldOrder: (id, data, admin = false) =>
    api.post(
      `${admin ? '/admin' : ''}/distributor/orders/${encodeURIComponent(id)}/held/cancel`,
      data,
      { skipAuthReplay: true }
    ),
  adminDeliveryQueue: (page = 1, options) =>
    api.get('/admin/distributor-deliveries', { params: { page }, signal: options?.signal }),
  retryPendingDelivery: (id, data) =>
    api.post(`/admin/distributor-deliveries/${encodeURIComponent(id)}/retry-pending`, data, {
      skipAuthReplay: true,
    }),
  reconcileDelivery: (id, data) =>
    api.post(`/admin/distributor-deliveries/${encodeURIComponent(id)}/reconcile`, data, {
      skipAuthReplay: true,
    }),
  adminBankOptions: () => api.get('/admin/distributor-bank'),
  adminBankAlertPreferences: () => api.get('/admin/distributor-bank/alert-preferences'),
  saveAdminBankAlertPreferences: (data) =>
    api.put('/admin/distributor-bank/alert-preferences', data, { skipAuthReplay: true }),
  adminBankAlerts: () => api.get('/admin/distributor-bank/alerts'),
  adminBankAlertDeliveries: (page = 1, options) =>
    api.get('/admin/distributor-bank/alert-deliveries', {
      params: { page },
      signal: options?.signal,
    }),
  reconcileBankAlertDelivery: (alertId, userId, data) =>
    api.post(
      `/admin/distributor-bank/alert-deliveries/${encodeURIComponent(alertId)}/recipients/${encodeURIComponent(userId)}/reconcile`,
      data,
      { skipAuthReplay: true }
    ),
  adminBankSummary: (id) => api.get(`/admin/distributor-bank/${encodeURIComponent(id)}`),
  adminAchMonitorHealth: (options) =>
    api.get('/admin/distributor-bank/ach-monitor-health', { signal: options?.signal }),
  adminReleaseMonitorHealth: (options) =>
    api.get('/admin/distributor-orders/release-monitor-health', { signal: options?.signal }),
  adminAchRecovery: (id, page = 1, options) =>
    api.get(`/admin/distributor-bank/${encodeURIComponent(id)}/ach-recovery`, {
      params: { page, limit: 20 },
      signal: options?.signal,
    }),
  reconcileAchReturn: (id, purchaseId, data) =>
    api.post(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}/reconcile-return`,
      data,
      { skipAuthReplay: true }
    ),
  paymentRecovery: (id, purchaseId, options) =>
    api.get(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}/payment-recovery`,
      { signal: options?.signal }
    ),
  merchantFunding: (id, purchaseId, options) =>
    api.get(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}/merchant-funding`,
      { signal: options?.signal }
    ),
  confirmMerchantFunding: (id, purchaseId, data) =>
    api.post(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}/merchant-funding`,
      data,
      { skipAuthReplay: true }
    ),
  merchantAdverseReview: (id, purchaseId, options) =>
    api.get(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}/merchant-adverse-review`,
      { signal: options?.signal }
    ),
  confirmMerchantAdverseReview: (id, purchaseId, data) =>
    api.post(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}/merchant-adverse-review`,
      data,
      { skipAuthReplay: true }
    ),
  recordPaymentSupportCase: (id, purchaseId, data) =>
    api.post(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}/payment-recovery`,
      data,
      { skipAuthReplay: true }
    ),
  adminCardRecovery: (id, page = 1, options) =>
    api.get(`/admin/distributor-bank/${encodeURIComponent(id)}/card-recovery`, {
      params: { page, limit: 20 },
      signal: options?.signal,
    }),
  reconcileCardReturn: (id, purchaseId, data) =>
    api.post(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}/reconcile-card-return`,
      data,
      { skipAuthReplay: true }
    ),
  recordCardChargeback: (id, purchaseId, data) =>
    api.post(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}/record-chargeback`,
      data,
      { skipAuthReplay: true }
    ),
  adminBankHistory: (id, page = 1) =>
    api.get(`/admin/distributor-bank/${encodeURIComponent(id)}/entries`, {
      params: { page, limit: 20 },
    }),
  adminPurchases: (id, page = 1) =>
    api.get(`/admin/distributor-bank/${encodeURIComponent(id)}/purchases`, {
      params: { page, limit: 20, status: 'prepared' },
    }),
  adminPurchase: (id, purchaseId) =>
    api.get(
      `/admin/distributor-bank/${encodeURIComponent(id)}/purchases/${encodeURIComponent(purchaseId)}`
    ),
  recordBankEntry: (id, data) =>
    api.post(`/admin/distributor-bank/${encodeURIComponent(id)}/entries`, data, {
      skipAuthReplay: true,
    }),
  getBankOperation: (id) => api.get(`/admin/distributor-bank/${encodeURIComponent(id)}/operation`),
  prepareBankOperation: (id, data) =>
    api.post(`/admin/distributor-bank/${encodeURIComponent(id)}/operation`, data, {
      skipAuthReplay: true,
    }),
  commitBankOperation: (id, operationId) =>
    api.post(
      `/admin/distributor-bank/${encodeURIComponent(id)}/operation/${encodeURIComponent(operationId)}/commit`,
      {},
      { skipAuthReplay: true }
    ),
  closeBankOperation: (id, operationId, action) =>
    api.post(
      `/admin/distributor-bank/${encodeURIComponent(id)}/operation/${encodeURIComponent(operationId)}/close`,
      { action },
      { skipAuthReplay: true }
    ),
  adminCashDeposits: (id, page = 1, options) =>
    api.get(`/admin/distributor-cash/${encodeURIComponent(id)}/deposits`, {
      ...options,
      params: { page, limit: 10 },
    }),
  adminCashWireClearance: (id, depositId, options) =>
    api.get(
      `/admin/distributor-cash/${encodeURIComponent(id)}/deposits/${encodeURIComponent(depositId)}/wire-clearance`,
      options
    ),
  confirmAdminCashWireClearance: (id, depositId, data) =>
    api.post(
      `/admin/distributor-cash/${encodeURIComponent(id)}/deposits/${encodeURIComponent(depositId)}/wire-clearance`,
      data,
      { skipAuthReplay: true }
    ),
  adminCashMerchantFunding: (id, depositId, options) =>
    api.get(
      `/admin/distributor-cash/${encodeURIComponent(id)}/deposits/${encodeURIComponent(depositId)}/merchant-funding`,
      options
    ),
  confirmAdminCashMerchantFunding: (id, depositId, data) =>
    api.post(
      `/admin/distributor-cash/${encodeURIComponent(id)}/deposits/${encodeURIComponent(depositId)}/merchant-funding`,
      data,
      { skipAuthReplay: true }
    ),
  adminCashAdverseReview: (id, depositId, options) =>
    api.get(
      `/admin/distributor-cash/${encodeURIComponent(id)}/deposits/${encodeURIComponent(depositId)}/merchant-adverse-review`,
      options
    ),
  confirmAdminCashAdverseReview: (id, depositId, data) =>
    api.post(
      `/admin/distributor-cash/${encodeURIComponent(id)}/deposits/${encodeURIComponent(depositId)}/merchant-adverse-review`,
      data,
      { skipAuthReplay: true }
    ),
  refreshAdminCashPayment: (id, depositId) =>
    api.post(
      `/admin/distributor-cash/${encodeURIComponent(id)}/deposits/${encodeURIComponent(depositId)}/payment-status`,
      {},
      { skipAuthReplay: true }
    ),
  mapBankProduct: (id, data) =>
    api.put(`/admin/distributor-bank/products/${encodeURIComponent(id)}/family`, data, {
      skipAuthReplay: true,
    }),
};

export default distributorService;
